import React from "react";
import { motion } from "framer-motion";
import LanguageLink from "../routing/LanguageLink";
import ScrollAnimation from "../../hooks/ScrollAnimation";
import SEOHead from "../section/SeoHead";
import HowItWorks from "../section/HowItWorks";
import ServicesHeroSection from "../section/ServicesHeroSection";
import ServicesFaqSection from "../section/ServicesFaqSection";
import NeedHelpRightNowSection from "../section/NeedHelpRightNowSection";
import {
  FiShield,
  FiDollarSign,
  FiUsers,
  FiFileText,
  FiPhone,
  FiBarChart,
  FiLifeBuoy,
  FiCheckCircle,
  FiMonitor,
} from "react-icons/fi";
import { useTranslation } from "react-i18next";

const Services = () => {
  const { t } = useTranslation();

  const getServiceRoute = (title) => {
    const routeMap = {
      "Form Assistance": "/services/form-assistance",
      "Lawyer Matching": "/services/lawyer-matching",
      "Payment Processing": "/services/payment-processing",
      "Legal Consultation": "/services/legal-consultation",
      "Document Review": "/services/document-review",
      "Case Tracking": "/services/case-tracking",
      "Immigration Support": "/services/immigration-support",
      "Legal Representation": "/services/legal-representation",
      "Online Consultation": "/services/online-consultation",
    };
    return routeMap[title] || "/services";
  };

  const servicesList = [
    { icon: <FiFileText className="w-8 h-8" />, key: 0, originalTitle: "Form Assistance" },
    { icon: <FiUsers className="w-8 h-8" />, key: 1, originalTitle: "Lawyer Matching" },
    { icon: <FiDollarSign className="w-8 h-8" />, key: 2, originalTitle: "Payment Processing" },
    { icon: <FiPhone className="w-8 h-8" />, key: 3, originalTitle: "Legal Consultation" },
    { icon: <FiShield className="w-8 h-8" />, key: 4, originalTitle: "Document Review" },
    { icon: <FiBarChart className="w-8 h-8" />, key: 5, originalTitle: "Case Tracking" },
    { icon: <FiLifeBuoy className="w-8 h-8" />, key: 6, originalTitle: "Immigration Support" },
    { icon: <FiCheckCircle className="w-8 h-8" />, key: 7, originalTitle: "Legal Representation" },
    { icon: <FiMonitor className="w-8 h-8" />, key: 8, originalTitle: "Online Consultation" },
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Immigration Services",
    description: t("services_section.subtitle"), // Utilizing translation for description
    provider: {
      "@type": "LocalBusiness",
      name: "Citizen Hub Immigration Services",
      telephone: "+1 3108802133",
      email: "info@citznhub.com",
    },
    areaServed: ["United States", "California", "New York", "Texas", "Florida"],
    offers: {
      "@type": "Offer",
      name: "Immigration Form Assistance",
      description: "Professional immigration form assistance and legal services across the United States",
    },
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title={`${t("services_section.title")} | Citizen Hub`}
        description={t("services_section.subtitle")}
        canonicalUrl="https://citizenhubimmigration.com/services"
        structuredData={structuredData}
      />
      {/* Hero Section */}
      <ServicesHeroSection />

      {/* Services Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollAnimation direction="up" delay={0.1}>
            <div className="text-center mb-16">
              <motion.h2
                className="text-3xl md:text-4xl font-bold text-gray-900 mb-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.6 }}
              >
                {t("services_section.title")}
              </motion.h2>
              <motion.p
                className="text-lg text-gray-600 max-w-2xl mx-auto"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.6 }}
              >
                {t("services_section.subtitle")}
              </motion.p>
            </div>
          </ScrollAnimation>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {servicesList.map((service, index) => (
              <ScrollAnimation key={index} direction="up" delay={index * 0.1}>
                {/* Passing originalTitle to getServiceRoute to preserve routing logic */}
                <LanguageLink to={getServiceRoute(service.originalTitle)}>
                  <motion.div
                    className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow cursor-pointer h-full"
                    whileHover={{ scale: 1.05, y: -5 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="text-blue-600 mb-4">{service.icon}</div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">
                      {t(`services_section.items.${index}.title`)}
                    </h3>
                    <p className="text-gray-600 mb-4">{t(`services_section.items.${index}.description`)}</p>
                    <ul className="space-y-2">
                      {(t(`services_section.items.${index}.features`, { returnObjects: true }) || []).map((feature, featureIndex) => (
                        <li
                          key={featureIndex}
                          className="flex items-center text-sm text-gray-600"
                        >
                          <span className="w-2 h-2 bg-blue-600 rounded-full mr-3"></span>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                </LanguageLink>
              </ScrollAnimation>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <HowItWorks />

      {/* CTA Section */}
      <NeedHelpRightNowSection />

      {/* FAQ Section */}
      <ServicesFaqSection />
    </div>
  );
};

export default Services;
